'use client'

import { useId, useRef, useState, type FormEvent } from 'react'
import Image from 'next/image'
import {
  EVENT_OFFER_NOTE,
  EVENT_PRICE_EUR,
  EVENT_TOPIC_FAQS,
  MAX_EVENT_QUANTITY,
  type ConferencePresentation,
} from '../lib/events'
import { DECLARED_SOURCE_OPTIONS } from '../lib/registrationOptions'
import s from './events.module.css'

export type ConferenceDate = {
  id: string
  dateLabel: string
  timeLabel: string
  durationLabel: string
  location: string
  mode: 'online' | 'in-person'
}

type ConferenceCardProps = {
  topic: string
  presentation: ConferencePresentation & { full: string }
  image?: { src: string; alt: string }
  dates: ConferenceDate[]
}

function CalendarIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2.5" y="4" width="15" height="13.5" rx="2.5" />
      <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" strokeLinecap="round" />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M10 17.5s5.5-5.1 5.5-9.1A5.5 5.5 0 0 0 4.5 8.4c0 4 5.5 9.1 5.5 9.1Z" strokeLinejoin="round" />
      <circle cx="10" cy="8.3" r="2" />
    </svg>
  )
}

function ScreenIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2.5" y="3.5" width="15" height="10.5" rx="2" />
      <path d="M7 17h6" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Une carte par conférence : visuel, présentation courte dépliable, formats
 * proposés et choix de la date. L'inscription est gratuite pendant l'offre de
 * fin d'année et part directement dans le suivi des participants.
 */
export default function ConferenceCard({ topic, presentation, image, dates }: ConferenceCardProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const attemptKey = useRef<string | null>(null)
  const descriptionId = useId()
  const [expanded, setExpanded] = useState(false)
  const [selectedId, setSelectedId] = useState(dates[0]?.id ?? '')
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [registered, setRegistered] = useState(false)

  const selected = dates.find((date) => date.id === selectedId) ?? dates[0]
  const hasInPerson = dates.some((date) => date.mode === 'in-person')
  const hasOnline = dates.some((date) => date.mode === 'online')
  const sessionCount = `${dates.length} séance${dates.length > 1 ? 's' : ''} disponible${dates.length > 1 ? 's' : ''}`
  const topicFaqs = EVENT_TOPIC_FAQS[topic] ?? []

  function openDialog() {
    setMessage('')
    setRegistered(false)
    attemptKey.current = null
    dialogRef.current?.showModal()
  }

  async function submitRegistration(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault()
    if (loading || !selected) return
    const form = formEvent.currentTarget
    if (!form.reportValidity()) return

    const formData = new FormData(form)
    attemptKey.current ??= window.crypto.randomUUID()

    setLoading(true)
    setMessage('')
    try {
      const response = await fetch('/api/registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: selected.id,
          quantity,
          firstName: String(formData.get('firstName') ?? '').trim(),
          lastName: String(formData.get('lastName') ?? '').trim(),
          email: String(formData.get('email') ?? '').trim(),
          phone: String(formData.get('phone') ?? '').trim(),
          declaredSource: String(formData.get('declaredSource') ?? '').trim(),
          newsletterOptIn: formData.get('newsletterOptIn') === 'oui',
          attemptId: attemptKey.current,
        }),
      })
      const result = await response.json() as { ok?: boolean; error?: string }
      if (!response.ok || !result.ok) throw new Error(result.error ?? 'inscription')
      setRegistered(true)
      // Mesure de la conversion sans transmettre de donnée personnelle.
      const analytics = window as unknown as { gtag?: (...args: unknown[]) => void }
      analytics.gtag?.('event', 'generate_lead', {
        method: 'conference-gratuite',
        event_id: selected.id,
        quantity,
      })
    } catch (error) {
      const detail = error instanceof Error && error.message !== 'inscription' ? error.message : ''
      setMessage(
        detail || 'L’inscription n’a pas pu être enregistrée. Merci de réessayer dans un instant, ou écrivez à contact@lena-lahor-coaching.fr.',
      )
    } finally {
      setLoading(false)
    }
  }

  if (!selected) return null

  return (
    <>
      <article className={s.conferenceCard} id={`theme-${presentation.index}`}>
        <div className={s.cardVisual}>
          <span className={s.cardBlob} aria-hidden="true" />
          {image ? (
            <figure className={s.cardFigure}>
              <Image
                src={image.src}
                alt={image.alt}
                width={800}
                height={800}
                sizes="(max-width: 700px) 130px, 210px"
                className={s.cardImage}
              />
            </figure>
          ) : null}
        </div>

        <div className={s.cardHead}>
          <p className={s.cardEyebrow}>
            <span className={s.cardEyebrowLong}>Conférence {presentation.index}</span>
            <span className={s.cardEyebrowShort}>Conf. {presentation.index}</span>
            <span className={s.cardEyebrowSep} aria-hidden="true">|</span>
            <span className={s.cardEyebrowCategory}>{presentation.category}</span>
          </p>
          <h3 className={s.cardTitle}>{topic}</h3>
          <ul className={s.cardTags}>
            {presentation.tags.map((tag) => <li key={tag}>{tag}</li>)}
          </ul>
        </div>

        <div className={s.cardDesc}>
          <p className={s.cardDescShort}>{presentation.short}</p>
          <div id={descriptionId} hidden={!expanded}>
            <p className={s.cardDescFull}>{presentation.full}</p>
          </div>
          <button
            className={s.readMore}
            type="button"
            aria-expanded={expanded}
            aria-controls={descriptionId}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Réduire la description' : 'Lire la suite de la description'}
            <span className={s.readMoreIcon} aria-hidden="true">⌄</span>
          </button>
        </div>

        <ul className={s.cardFormats}>
          {hasInPerson ? (
            <li><PinIcon /> En présentiel à Lyon</li>
          ) : null}
          {hasOnline ? (
            <li><ScreenIcon /> En visioconférence</li>
          ) : null}
        </ul>

        <div className={s.cardBooking}>
          <p className={s.cardBookingHead}>
            <span className={s.cardBookingTitle}><CalendarIcon /> Les prochaines dates</span>
            <span className={s.cardBookingCount}>{sessionCount}</span>
          </p>

          <select
            id={`${descriptionId}-date`}
            className={s.cardSelect}
            aria-label={`Choisissez la date de votre conférence : ${topic}`}
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            {dates.map((date) => (
              <option key={date.id} value={date.id}>
                {date.dateLabel} · {date.timeLabel} · {date.mode === 'online' ? 'En ligne' : 'Lyon Confluence'}
              </option>
            ))}
          </select>

          <button
            className={s.cardCta}
            type="button"
            onClick={openDialog}
            aria-haspopup="dialog"
            aria-label={`S’inscrire à ${topic}, le ${selected.dateLabel} à ${selected.timeLabel}, ${selected.location}`}
          >
            Je m’inscris gratuitement <span aria-hidden="true">→</span>
          </button>

          <p className={s.cardOffer}>
            <s>{EVENT_PRICE_EUR} €</s> <strong>0 €</strong> · {EVENT_OFFER_NOTE}
          </p>

          {topicFaqs.length > 0 ? (
            <details className={s.cardFaq}>
              <summary>FAQ de cette conférence</summary>
              <div className={s.cardFaqList}>
                {topicFaqs.map((faq) => (
                  <details className={s.cardFaqItem} key={faq.question}>
                    <summary>{faq.question}</summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      </article>

      <dialog ref={dialogRef} className={s.bookingDialog} aria-labelledby={`booking-title-${selected.id}`}>
        {registered ? (
          <div className={s.registrationSuccess} role="status">
            <div className={s.confirmationStatus}>
              <span className={s.confirmationIcon} aria-hidden="true">✓</span>
              <span>Inscription confirmée</span>
            </div>
            <span className={s.confirmationEyebrow}>Léna Lahor · Événements</span>
            <h2 id={`booking-title-${selected.id}`}>Merci, votre place est <em>réservée</em></h2>
            <p className={s.confirmationLead}>
              Votre inscription est enregistrée. <strong>Vous n’avez rien à payer</strong> : cette conférence vous est offerte.
            </p>

            <section className={s.confirmationDetails} aria-label="Récapitulatif de votre inscription">
              <div className={s.confirmationTopic}>
                <span>Conférence</span>
                <strong>{topic}</strong>
              </div>
              <div>
                <span>Date et heure</span>
                <strong>{selected.dateLabel} · {selected.timeLabel}</strong>
              </div>
              <div>
                <span>Durée</span>
                <strong>{selected.durationLabel}</strong>
              </div>
              <div>
                <span>{selected.mode === 'online' ? 'Format' : 'Lieu'}</span>
                <strong>{selected.mode === 'online' ? 'En ligne' : selected.location}</strong>
              </div>
              <div>
                <span>Places réservées</span>
                <strong>{quantity} {quantity === 1 ? 'place' : 'places'}</strong>
              </div>
            </section>

            <div className={s.confirmationNextStep}>
              <span>À retenir</span>
              <p>
                {selected.mode === 'online'
                  ? 'La veille de la conférence, vous recevrez un e-mail complémentaire avec toutes les informations pratiques et de dernière minute : le lien de connexion pour la rejoindre en ligne.'
                  : 'La veille de la conférence, vous recevrez un e-mail complémentaire avec toutes les informations pratiques et de dernière minute : l’adresse exacte du lieu de rencontre.'}
              </p>
            </div>

            <div className={s.confirmationNextStep}>
              <span>Et si vous ne pouvez plus venir ?</span>
              <p>
                La place étant offerte, il n’y a rien à rembourser. Prévenez-moi au plus tôt à{' '}
                <a href="mailto:contact@lena-lahor-coaching.fr">contact@lena-lahor-coaching.fr</a> : votre place peut profiter à quelqu’un d’autre.
              </p>
            </div>

            <div className={s.dialogActions}>
              <button className={s.bookButton} type="button" onClick={() => dialogRef.current?.close()}>
                Fermer
              </button>
            </div>
          </div>
        ) : (
          <form className={s.bookingForm} onSubmit={submitRegistration} onChangeCapture={() => { attemptKey.current = null }}>
            <button
              className={s.dialogClose}
              type="button"
              onClick={() => dialogRef.current?.close()}
              aria-label="Fermer le formulaire"
            >
              ×
            </button>
            <span className={s.eyebrow}>Inscription gratuite</span>
            <h2 id={`booking-title-${selected.id}`}>{topic}</h2>
            <p className={s.dialogEventDetails}>
              {selected.dateLabel} · {selected.timeLabel} · {selected.location}
            </p>
            <p className={s.dialogOffer}>
              <span className={s.priceOld}>{EVENT_PRICE_EUR} €</span>
              <strong>0 €</strong> — offerte exceptionnellement jusqu’au 31 décembre 2026
            </p>

            <div className={s.bookingFields}>
              <label className={s.inputLabel}>
                Prénom <span aria-hidden="true">*</span>
                <input className={s.textInput} name="firstName" type="text" autoComplete="given-name" maxLength={80} required />
              </label>
              <label className={s.inputLabel}>
                Nom <span aria-hidden="true">*</span>
                <input className={s.textInput} name="lastName" type="text" autoComplete="family-name" maxLength={80} required />
              </label>
              <label className={s.inputLabel}>
                Adresse e-mail <span aria-hidden="true">*</span>
                <input className={s.textInput} name="email" type="email" autoComplete="email" maxLength={254} required />
              </label>
              <label className={s.inputLabel}>
                Téléphone <span aria-hidden="true">*</span>
                <input className={s.textInput} name="phone" type="tel" autoComplete="tel" inputMode="tel" maxLength={32} pattern="[0-9+() .-]{8,32}" title="Saisissez un numéro de téléphone valide." required />
              </label>
            </div>

            <label className={s.quantityLabel}>
              Nombre de places
              <select
                className={s.quantitySelect}
                value={quantity}
                onChange={(event) => setQuantity(Number(event.target.value))}
                aria-label="Nombre de places à réserver"
              >
                {Array.from({ length: MAX_EVENT_QUANTITY }, (_, index) => index + 1).map((count) => (
                  <option key={count} value={count}>{count} {count === 1 ? 'place' : 'places'}</option>
                ))}
              </select>
            </label>

            <label className={s.quantityLabel}>
              Comment m’avez-vous connu ? <span aria-hidden="true">*</span>
              <select
                className={s.quantitySelect}
                name="declaredSource"
                defaultValue=""
                required
                aria-label="Comment avez-vous connu cette conférence ?"
              >
                <option value="" disabled>Sélectionnez une réponse</option>
                {DECLARED_SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className={s.consentLabel}>
              <input className={s.consentCheckbox} type="checkbox" name="newsletterOptIn" value="oui" />
              <span>
                J’accepte de recevoir par e-mail les prochaines dates de conférences, d’ateliers et de cercles de bien-être.
                Je peux me désinscrire à tout moment. <em>Facultatif.</em>
              </span>
            </label>

            <p className={s.privacyNotice}>
              Vos coordonnées sont demandées pour enregistrer votre inscription et vous transmettre les informations pratiques. Consultez la{' '}
              <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer">politique de confidentialité</a>.
            </p>

            <p className={s.bookingPolicy}>
              La conférence est offerte : rien à payer, ni maintenant ni sur place. Si vous ne pouvez plus venir, prévenez-moi au plus tôt afin de libérer la place.{' '}
              <a href="/conditions-annulation" target="_blank" rel="noopener noreferrer">Voir les conditions d’annulation</a>
            </p>

            {message ? (
              <p className={s.bookingMessage} role="alert">{message}</p>
            ) : null}

            <div className={s.dialogActions}>
              <button className={s.cancelButton} type="button" onClick={() => dialogRef.current?.close()}>Annuler</button>
              <button className={s.bookButton} type="submit" disabled={loading}>
                {loading ? 'Enregistrement…' : 'Valider mon inscription'}
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  )
}
